if (!customElements.get('product-tabs')) {
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('.product-tabs__nav');
      this.wrap = this.querySelector('.product-tabs__nav-wrap');
      if (!this.nav) return;

      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      if (!this.tabs.length) return;

      this.onNavClick = this.handleNavClick.bind(this);
      this.onNavKeydown = this.handleNavKeydown.bind(this);
      this.onNavScroll = this.updateOverflow.bind(this);
      this.onMoreClick = this.handleMoreClick.bind(this);

      this.nav.addEventListener('click', this.onNavClick);
      this.nav.addEventListener('keydown', this.onNavKeydown);
      this.nav.addEventListener('scroll', this.onNavScroll, { passive: true });
      this.addEventListener('click', this.onMoreClick);

      this.resizeObserver = new ResizeObserver(() => {
        this.updateOverflow();
        this.initExcerpt(this.getActivePanel());
      });
      this.resizeObserver.observe(this.nav);

      const openTab = this.tabs.find((tab) => tab.hasAttribute('data-open')) || this.tabs[0];
      if (openTab) this.activateTab(openTab, false);

      this.updateOverflow();

      if (window.Shopify && Shopify.designMode) {
        this.onBlockSelect = this.handleBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      if (this.nav) {
        this.nav.removeEventListener('click', this.onNavClick);
        this.nav.removeEventListener('keydown', this.onNavKeydown);
        this.nav.removeEventListener('scroll', this.onNavScroll);
      }

      this.removeEventListener('click', this.onMoreClick);

      if (this.resizeObserver) this.resizeObserver.disconnect();

      if (this.onBlockSelect) {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    /**
     * Handles clicks on the tab list.
     * @param {MouseEvent} evt
     */
    handleNavClick(evt) {
      const tab = evt.target.closest('[role="tab"]');
      if (!tab || !this.nav.contains(tab)) return;
      this.activateTab(tab, true);
    }

    /**
     * Handles arrow-key navigation between tabs.
     * @param {KeyboardEvent} evt
     */
    handleNavKeydown(evt) {
      const currentIndex = this.tabs.indexOf(evt.target);
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;
      if (evt.key === 'ArrowRight' || evt.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % this.tabs.length;
      } else if (evt.key === 'ArrowLeft' || evt.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      } else if (evt.key === 'Home') {
        nextIndex = 0;
      } else if (evt.key === 'End') {
        nextIndex = this.tabs.length - 1;
      } else {
        return;
      }

      evt.preventDefault();
      const nextTab = this.tabs[nextIndex];
      this.activateTab(nextTab, true);
      nextTab.focus();
    }

    /**
     * Activates a tab and shows its panel.
     * @param {HTMLElement} tab
     * @param {boolean} [scroll=true]
     */
    activateTab(tab, scroll = true) {
      if (!tab) return;

      this.tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        item.tabIndex = selected ? 0 : -1;
      });

      const panelId = tab.getAttribute('aria-controls');
      this.panels.forEach((panel) => {
        panel.hidden = panel.id !== panelId;
      });

      requestAnimationFrame(() => this.initExcerpt(this.getActivePanel()));

      if (scroll) {
        tab.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        requestAnimationFrame(() => this.updateOverflow());
      }
    }

    /**
     * Shows edge fades when the tab list overflows.
     */
    updateOverflow() {
      if (!this.nav || !this.wrap) return;

      const maxScroll = this.nav.scrollWidth - this.nav.clientWidth;
      this.wrap.classList.toggle('is-overflow-start', this.nav.scrollLeft > 2);
      this.wrap.classList.toggle('is-overflow-end', this.nav.scrollLeft < maxScroll - 2);
    }

    /**
     * Returns the currently visible tab panel.
     * @returns {HTMLElement|undefined}
     */
    getActivePanel() {
      return this.panels.find((panel) => !panel.hidden);
    }

    /**
     * Toggles collapsed tab content.
     * @param {MouseEvent} evt
     */
    handleMoreClick(evt) {
      const button = evt.target.closest('.product-tabs__more');
      if (!button || !this.contains(button)) return;

      const panel = button.closest('.product-tabs__panel');
      if (!panel) return;

      this.setExcerptExpanded(panel, panel.dataset.expanded !== 'true');
    }

    /**
     * Collapses a panel when its content is taller than the excerpt height.
     * @param {HTMLElement|undefined} panel
     */
    initExcerpt(panel) {
      if (!panel || panel.hidden) return;

      const excerpt = panel.querySelector('.product-tabs__excerpt');
      const button = panel.querySelector('.product-tabs__more');
      if (!excerpt || !button) return;
      if (excerpt.clientHeight === 0 && panel.dataset.expanded !== 'true') return;

      if (panel.dataset.expanded === 'true') {
        this.setExcerptExpanded(panel, true);
        return;
      }

      panel.classList.add('is-collapsed');
      const overflows = excerpt.scrollHeight > excerpt.clientHeight + 4;

      if (overflows) {
        button.hidden = false;
        this.setExcerptExpanded(panel, false);
      } else {
        panel.classList.remove('is-collapsed');
        button.hidden = true;
      }
    }

    /**
     * Expands or collapses a panel excerpt.
     * @param {HTMLElement} panel
     * @param {boolean} expanded
     */
    setExcerptExpanded(panel, expanded) {
      const button = panel.querySelector('.product-tabs__more');
      const expandLabel = button && button.querySelector('[data-expand]');
      const collapseLabel = button && button.querySelector('[data-collapse]');
      if (!button) return;

      panel.dataset.expanded = expanded ? 'true' : 'false';
      panel.classList.toggle('is-collapsed', !expanded);
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');

      if (expandLabel) expandLabel.hidden = expanded;
      if (collapseLabel) collapseLabel.hidden = !expanded;
    }

    /**
     * Activates the matching tab when a block is selected in the theme editor.
     * @param {Event} evt
     */
    handleBlockSelect(evt) {
      if (!evt.target || !this.contains(evt.target)) return;

      const panel = evt.target.matches('[role="tabpanel"]')
        ? evt.target
        : evt.target.closest('[role="tabpanel"]');
      if (!panel) return;

      const tab = this.tabs.find((item) => item.getAttribute('aria-controls') === panel.id);
      if (tab) this.activateTab(tab, true);
    }
  }

  customElements.define('product-tabs', ProductTabs);
}
